import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  ChevronButton,
  StyledIcon,
  StyledText,
} from "./app-bar-account-menu.style";
import AccountIcon from "@iconify/icons-mdi/account-cog";
import chevronDownIcon from "@iconify/icons-mdi/chevron-down";
import LogoutIcon from "@iconify/icons-mdi/logout";
import { InlineIcon } from "@iconify/react";
import { useVersions } from "oc/hooks/use-versions";
import { ocNarrowMenuState } from "states";

// Fallback static versions (used while loading or on error)
import jsonData from "jdapi_versions.json";
import jsonOcData from "oc_versions.json";

type OcAppBarAccountMenuProps = {
  onLogout: () => void;
  currentVersion?: string;
  isMobile: boolean;
};

export const OcAppBarAccountMenu = ({
  onLogout,
  currentVersion,
  isMobile,
}: OcAppBarAccountMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const history = useHistory();
  const [narrowMenuOpened, setNarrowMenuOpened] =
    useRecoilState(ocNarrowMenuState);

  // Fetch runtime versions from backend (with fallback to static JSON)
  const { data: versionsData } = useVersions();
  const versions = versionsData?.versions || {
    oc: jsonOcData?.oc || currentVersion || "...",
    jdapi: jsonData?.jdapi || "...",
    munkaugy: jsonData?.munkaugy || "...",
    jelenlet: jsonData?.jelenlet || "...",
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyProfile = () => {
    if (isMobile) {
      setNarrowMenuOpened(!narrowMenuOpened);
    }
    setAnchorEl(null);
    history.push("/my-hr/my-profile");
  };

  const handleLogout = () => {
    setAnchorEl(null);
    onLogout();
  };

  const [showAbout, setShowAbout] = useState(false);

  const handleAbout = () => {
    setShowAbout((prev) => !prev);
    setAnchorEl(null);
  };

  return (
    <div>
      <ChevronButton onClick={handleClick}>
        <InlineIcon height="24px" color="#5E6366" icon={chevronDownIcon} />
      </ChevronButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{
          vertical: 56,
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            border: "2px solid",
            borderColor: (theme) => theme.palette.gray40.main,
            borderRadius: "4px",
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuItem id="show-my-profile" onClick={handleMyProfile}>
          <StyledIcon>
            <InlineIcon
              width="24px"
              icon={AccountIcon}
            />
          </StyledIcon>
          <StyledText>{t("MY_HR_MY_PROFILE")}</StyledText>
        </MenuItem>
        <MenuItem id="show-about" onClick={handleAbout}>
          <StyledIcon>
            <svg width="24px" height="24px" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.1215 4.00052C8.37435 3.99896 5.26467 4.00104 5.21104 4.00518C4.93559 4.02643 4.49967 4.08641 4.26866 4.13485C2.98519 4.40409 2.41157 4.97223 2.14851 6.23481C2.04295 6.74145 2.02215 7.01312 2.01067 8.0366L2 8.99272L2.45197 9.00653C7.14026 9.15065 11.2609 12.2508 12.6812 16.7021C13.0099 17.7324 13.2047 18.9271 13.2047 19.9128V20.2353L15.9396 20.2259C18.6848 20.2166 18.8314 20.2127 19.3894 20.1339C20.0629 20.0389 20.6561 19.8272 21.0143 19.554C21.5828 19.1203 21.8965 18.2789 21.9782 16.9687C22.0073 16.5016 22.0073 7.69941 21.9782 7.23229C21.9407 6.63104 21.8706 6.21279 21.7379 5.79799C21.3885 4.70633 20.7518 4.26655 19.1945 4.04103C18.9525 4.00599 18.4657 4.00319 12.1215 4.00052V4.00052ZM12.0168 6.87454H18.2306V12.0246V17.1746H16.3482H14.466L14.4512 17.0958C14.4431 17.0525 14.3889 16.8501 14.3309 16.6459C13.8276 14.8752 12.9804 13.2812 11.7996 11.8837C11.5139 11.5455 10.8713 10.8887 10.5354 10.5916C9.25356 9.45767 7.7963 8.60989 6.20228 8.07085L5.77452 7.92625L5.77435 7.41485C5.77431 7.13358 5.78077 6.89688 5.78874 6.88895C5.79668 6.88099 8.59933 6.87454 12.0168 6.87454H12.0168ZM2.02659 10.5976V11.3895V12.1813L2.32437 12.1963C2.48821 12.2045 2.75296 12.2258 2.91266 12.2438C6.62577 12.6623 9.54555 15.585 9.96368 19.3018C9.98167 19.4617 10.003 19.7267 10.0112 19.8907L10.0261 20.1888H10.8172H11.6082L11.5954 19.7713C11.5435 18.0808 10.9703 16.3235 9.99145 14.8544C9.31092 13.833 8.37632 12.8975 7.35566 12.2162C5.88462 11.2341 4.13996 10.664 2.44359 10.6108L2.02659 10.5976ZM2.02659 13.7918V14.5942V15.3966H2.19787C2.59752 15.3966 3.23462 15.5217 3.68376 15.6884C5.19549 16.2494 6.31964 17.5223 6.69125 19.0937C6.76087 19.3881 6.81402 19.7877 6.81402 20.0165V20.1888H7.6189H8.42361L8.40862 19.9015C8.37268 19.2156 8.24559 18.5879 8.01592 17.9624C7.90815 17.6689 7.63092 17.1037 7.45404 16.8169C6.58199 15.4028 5.14736 14.3637 3.53211 13.9761C3.08301 13.8684 2.51475 13.7926 2.15114 13.7921L2.02659 13.7918ZM2.02659 16.9947V18.5918V20.1888H3.61881H5.21104L5.21095 20.0316C5.21069 19.5797 5.04193 19.0166 4.76821 18.5543C4.23934 17.6611 3.27203 17.0623 2.27027 17.0079L2.02659 16.9947Z"
                fill="#4D4D4D"
              />
            </svg>
          </StyledIcon>
          <StyledText>{t("MY_HR_ABOUT")}</StyledText>
        </MenuItem>
        <MenuItem id="logout" onClick={handleLogout}>
          <StyledIcon>
            <InlineIcon
              width="24px"
              icon={LogoutIcon}
            />
          </StyledIcon>
          <StyledText>{t("MY_HR_LOGOUT")}</StyledText>
        </MenuItem>
      </Menu>
      <Dialog
        id="oc-app-bar-dialog"
        open={showAbout}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            return false;
          }
          handleAbout();
        }}
        disableEscapeKeyDown
        scroll="body"
        PaperProps={{
          style: {
            padding: "0px 24px 24px 24px",
            width: isMobile ? "auto" : 350,
          },
        }}
      >
        <Box textAlign="center" fontSize="24px" fontFamily="Mulish" p={3}>
          {t("MY_HR_ABOUT")}
        </Box>
        <DialogContent>
          {[
            { label: "Munkaügy", value: versions.munkaugy },
            { label: "Jelenlét", value: versions.jelenlet },
            { label: "JDolBerApiGateway", value: versions.jdapi },
            { label: "OrgwareConnect", value: versions.oc },
          ].map(({ label, value }) => (
            <Box
              key={label}
              display="flex"
              flexDirection="row"
              alignItems="center"
              paddingBottom="10px"
            >
              <Box
                width="50%"
                textAlign={isMobile ? "left" : "right"}
                pr={1}
                fontSize="12px"
                letterSpacing="0.4px"
              >
                {label}
              </Box>
              <Box
                width="50%"
                textAlign={isMobile ? "right" : "left"}
                pl={1}
                fontSize="16px"
                letterSpacing="0.44px"
              >
                {value}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <Box height="16px" />
        <DialogActions>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleAbout}
          >
            {t("OC_ALL_RIGHT")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OcAppBarAccountMenu;
